import { defineStore } from 'pinia'
import registry from '@/generated/feature-flags.json'
import { useAppStore } from './app'
import { useTenantsStore } from './tenants'

/**
 * The product feature flags (MYAC-1190). Which flags exist, their default and
 * whether a company may override them are NOT decided here: they come from the
 * registry in core_models_services, through the generated
 * `src/generated/feature-flags.json` (regenerate it, never edit it — see
 * `docs/feature-flags.md` in the workspace). A flag's value lives in Firestore.
 */
export type FeatureFlagKey = keyof typeof registry.flags

/**
 * Resolve a feature flag with per-tenant override precedence (MYAC-837):
 *   effective(flag) = tenantOverride ?? global
 * `??` treats `null`/absent as "defer to global" and an explicit `false` as
 * force-off — so the tri-state and the "no current tenant" case (resolves to
 * global) both fall out for free.
 */
export function resolveFeatureFlag(key: string, global: boolean): boolean {
  const override = useTenantsStore().currentTenant?.feature_flags?.[key]
  return override ?? global
}

/**
 * Effective value of a registry flag: the company's override when the registry
 * allows one, else the global `system/public_config` value, else the registry
 * default. Indexing `publicConfig` by the key keeps `PublicConfig` in step at
 * compile time — a registry flag with no field there fails the type check.
 */
export function isFeatureFlagOn(key: FeatureFlagKey): boolean {
  const spec = registry.flags[key]
  const global = useAppStore().publicConfig?.[key] ?? spec.default
  return spec.tenant_overridable ? resolveFeatureFlag(key, global) : global
}

export const useFeatureFlagsStore = defineStore('featureFlags', {
  state: () => ({
  }),
  getters: {
    isSelfServeEnabled: (): boolean => isFeatureFlagOn('self_serve_enabled'),
    isAssessmentSchedulingEnabled: (): boolean => isFeatureFlagOn('enable_assessment_scheduling'),
    isLegacyPlansEnabled: (): boolean => isFeatureFlagOn('enable_legacy_plans'),
    isTdAppliedStandardsEnabled: (): boolean => isFeatureFlagOn('enable_td_applied_standards'),
    isTdDeviceFeaturesEnabled: (): boolean => isFeatureFlagOn('enable_td_device_features'),
    isQmsSuppliersCustomersEnabled: (): boolean => isFeatureFlagOn('enable_qms_suppliers_customers'),
    isQmsNcCapaEnabled: (): boolean => isFeatureFlagOn('enable_qms_nc_capa'),
    isTaskDirectivesEnabled: (): boolean => isFeatureFlagOn('enable_task_directives'),
    isSmartRerunEnabled: (): boolean => isFeatureFlagOn('enable_smart_rerun'),
    isPlaybookEnabled: (): boolean => isFeatureFlagOn('enable_playbook'),
    isTracexExportEnabled: (): boolean => isFeatureFlagOn('enable_tracex_export'),
    isCommandQueueProducersEnabled: (): boolean => isFeatureFlagOn('enable_command_queue_producers'),
    isRiskAnalysisEnabled: (): boolean => isFeatureFlagOn('enable_risk_analysis'),
    isTenantConnectionsEnabled: (): boolean => isFeatureFlagOn('enable_tenant_connections'),
    /** MYAC-918 — your conversations: the handle's list, the column on the
     *  conversation page, the title heading, rename, the personal read state. */
    isConversationsEnabled: (): boolean => isFeatureFlagOn('enable_conversations'),
  },
  actions: {
  }
})
